<template>
  <div>
    <div class="q-mb-md q-mx-sm row q-gutter-sm items-center">
      <q-badge
        outline
        class="q-pa-xs"
        :class="{ 'cursor-pointer': editable }"
        style="font-size: 14px"
        :style="{ 'cursor': editable ? 'se-resize' : 'not-allowed', }"
        :label="openCount + ' Open'"
        :title="editable ? 'Click to Open All': ''"
        color="primary"
        @mousedown="editable && setAll(true)"
        @mouseover="(event) => editable && setAll(true, event)"
        @mouseout="previewingAction = false"
      />
      <q-badge
        class="q-pa-xs"
        :class="{ 'cursor-pointer': editable }"
        style="font-size: 14px"
        :style="{ 'cursor': editable ? 'se-resize' : 'not-allowed', }"
        :label="closedCount + ' Closed'"
        :title="editable ? 'Click to Close All': ''"
        color="dark"
        @mousedown="editable && setAll(false)"
        @mouseover="(event) => editable && setAll(false, event)"
        @mouseout="previewingAction = false"
      />
      <div v-if="editable">
        Click and draw to change schedule. Click headings to toggle rows or columns.
      </div>
    </div>
    <q-markup-table
      separator="cell"
      flat
      bordered
      class="non-selectable"
    >
      <thead>
        <tr>
          <th
            :class="{ 'bg-grey-4': readOnly }"
            :style="{ 'cursor': editable ? 'se-resize' : 'not-allowed', }"
            @mousedown="editable && toggleAll()"
            @mouseover="(event) => editable && toggleAll(event)"
            @mouseout="previewingAction = false"
          >
            Time Slot
          </th>
          <th
            v-for="day in currentCircuit.daysOfWeek"
            :key="day"
            :class="{ 'bg-grey-4': readOnly }"
            :style="{ 'cursor': editable ? 's-resize' : 'not-allowed' }"
            @mousedown="editable && toggleDay(day)"
            @mouseover="(event) => editable && toggleDay(day, event)"
            @mouseout="previewingAction = false"
          >
            {{ day }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="timeSlot in timeSlots.data"
          :key="timeSlot.id"
          class="q-tr--no-hover"
        >
          <td
            :class="{ 'bg-grey-4': readOnly }"
            style="width: 20%"
            :style="{ 'cursor': editable ? 'e-resize' : 'not-allowed' }"
            @mousedown="editable && toggleTimeSlot(timeSlot.id)"
            @mouseover="(event) => editable && toggleTimeSlot(timeSlot.id, event)"
            @mouseout="previewingAction = false"
          >
            {{ timeSlot.name }}
          </td>
          <td
            v-for="day in currentCircuit.daysOfWeekNumbers"
            :key="day"
            style="width: 10%"
            :style="{ 'cursor': editable ? 'cell' : 'not-allowed' }"
            class="showing text-center"
            :class="{ 'selected': isShowtimeOpenDisplay(timeSlot.id, day), 'preview': isShowtimePreview(timeSlot.id, day) }"
            @mousedown="editable && toggle(timeSlot.id, day)"
            @mouseover="(event) => editable && toggle(timeSlot.id, day, event)"
            @mouseout="previewingAction = false"
          >
            <q-icon
              v-if="isShowtimeOpenDisplay(timeSlot.id, day) || isShowtimePreview(timeSlot.id, day)"
              name="check"
              color="primary"
              size="xs"
              style="pointer-events: none;"
            />
          </td>
        </tr>
      </tbody>
    </q-markup-table>
  </div>
</template>

<script>

import { date } from 'quasar'

export default {
  name: 'SiteSchedule',
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      required: false,
      default: 'edit',
      validator: function (value) {
        return _.includes(['edit', 'read-only'], value)
      }
    }
  },
  emits: ['update:model-value'],
  data: function () {
    return {
      date,
      schedule: {},
      previewSchedule: {},
      toggleValue: false,
      previewingAction: false
    }
  },
  computed: {
    openCount () {
      return _(this.displaySchedule).values().map(_.values).flatten().filter().value().length
    },
    closedCount () {
      return _(this.displaySchedule).values().map(_.values).flatten().filter(v => !v).value().length
    },
    readOnly () {
      return this.mode === 'read-only'
    },
    editable () {
      return this.mode === 'edit'
    },
    displaySchedule () {
      if (this.previewingAction) {
        return this.previewSchedule
      } else {
        return this.schedule
      }
    }
  },
  watch: {
    timeSlots: {
      handler () {
        _.each(this.timeSlots.data, (timeSlot) => {
          if (!this.schedule[timeSlot.id]) {
            this.schedule[timeSlot.id] = {}
          }
          _.each(this.currentCircuit.daysOfWeekNumbers, (number) => {
            this.schedule[timeSlot.id][number] = !!this.modelValue[timeSlot.id]?.[number]
          })
        })
      },
      immediate: true
    },
    modelValue: {
      handler () {
        if (_.isEqual(this.modelValue, this.schedule)) {
          return
        }

        _.each(this.timeSlots.data, (timeSlot) => {
          if (!this.schedule[timeSlot.id]) {
            this.schedule[timeSlot.id] = {}
          }
          _.each(this.currentCircuit.daysOfWeekNumbers, (number) => {
            this.schedule[timeSlot.id][number] = !!this.modelValue[timeSlot.id]?.[number]
          })
        })
      },
      immediate: true
    },
    schedule: {
      handler () {
        this.$emit('update:model-value', this.schedule)
      },
      deep: true
    }
  },
  methods: {
    setAll (value, event) {
      let schedule
      if (event && !event.buttons) {
        this.previewingAction = true
        this.previewSchedule = _.cloneDeep(this.schedule)
        schedule = this.previewSchedule
      } else {
        this.previewingAction = false
        schedule = this.schedule
      }
      _.each(this.timeSlots.data, (timeSlot) => {
        schedule[timeSlot.id] = {}
        _.each(this.currentCircuit.daysOfWeekNumbers, (number) => {
          schedule[timeSlot.id][number] = value
        })
      })
    },
    toggleAll (event) {
      this.setAll(!_(this.schedule).values().map(_.values).flatten().first(), event)
    },
    toggleDay (day, event) {
      let schedule
      if (event && !event.buttons) {
        this.previewingAction = true
        this.previewSchedule = _.cloneDeep(this.schedule)
        schedule = this.previewSchedule
      } else {
        this.previewingAction = false
        schedule = this.schedule
      }

      let value = null
      _.each(this.timeSlots.data, (timeSlot) => {
        if (!schedule[timeSlot.id]) {
          schedule[timeSlot.id] = {}
        }
        const number = this.currentCircuit.daysOfWeekNumbers[this.currentCircuit.daysOfWeek.indexOf(day)]
        if (value === null) {
          value = !schedule[timeSlot.id][number]
        }
        schedule[timeSlot.id][number] = value
      })
    },
    toggleTimeSlot (timeSlotId, event) {
      let schedule
      if (event && !event.buttons) {
        this.previewingAction = true
        this.previewSchedule = _.cloneDeep(this.schedule)
        schedule = this.previewSchedule
      } else {
        this.previewingAction = false
        schedule = this.schedule
      }

      let value = null
      if (!schedule[timeSlotId]) {
        schedule[timeSlotId] = {}
      }
      _.each(this.currentCircuit.daysOfWeekNumbers, (number) => {
        if (value === null) {
          value = !schedule[timeSlotId][number]
        }
        schedule[timeSlotId][number] = value
      })
    },
    toggle (timeSlotId, day, event) {
      let schedule
      if (event && !event.buttons) {
        this.previewingAction = true
        this.previewSchedule = _.cloneDeep(this.schedule)
        schedule = this.previewSchedule
      } else {
        schedule = this.schedule
        this.previewingAction = false
      }
      if (!event || this.previewingAction) {
        schedule[timeSlotId][day] = !schedule[timeSlotId][day]
        this.toggleValue = schedule[timeSlotId][day]
      } else {
        schedule[timeSlotId][day] = this.toggleValue
      }
    },
    isShowtimeOpenDisplay (itemSlotId, dayNumber) {
      let schedule
      if (this.previewingAction) {
        schedule = this.previewSchedule
      } else {
        schedule = this.schedule
      }
      return schedule[itemSlotId] && schedule[itemSlotId][dayNumber]
    },
    isShowtimePreview (itemSlotId, dayNumber) {
      if (!this.previewingAction) {
        return false
      }
      return (this.schedule[itemSlotId] && this.schedule[itemSlotId][dayNumber]) !== (this.previewSchedule[itemSlotId] && this.previewSchedule[itemSlotId][dayNumber])
    }
  },
  configuration: ['currentCircuit', 'timeSlots']
}
</script>

<style lang="scss" scoped>

.showing {
  background-color: #AAA;
}

.showing.selected {
  background-color: #FFF;
}

.showing.preview {
  background-color: #CCC;
}

</style>
